import { useMutation } from '@apollo/client/react';
import type { FC } from 'react';

import type { ProductAttribute } from '@zen/Products/components/forms/ProductAttributeSelect/types';
import { ProductAttributesTrackingAction, ProductAttributesTrackingCategory } from '@zen/Products/types';
import { useNotification } from '@zen/utils/hooks/useNotification';
import useTracking from '@zen/utils/hooks/useTracking';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import ProductAttributeForm from '../components/ProductAttributeForm';
import {
  CreateProductAttributeDocument,
  type CreateProductAttributeMutationResult,
  type CreateProductAttributeMutationVariables
} from '../graphql';
import type { CoreProductAttribute } from '../types';

interface Props {
  accountId: string;
  onClose: () => void;
  onSuccess?: (attribute: ProductAttribute) => void;
  source?: 'ProductForm' | 'ProductSettings';
}

const CreateProductAttribute: FC<Props> = ({ accountId, onClose, onSuccess, source = 'ProductSettings' }) => {
  const { addSuccess, addError } = useNotification();
  const { trackEvent } = useTracking();
  const [createAttribute] = useMutation<CreateProductAttributeMutationResult, CreateProductAttributeMutationVariables>(
    CreateProductAttributeDocument
  );

  const trackAttributeCreation = ({ name, type }: Pick<CoreProductAttribute, 'name' | 'type'>): void => {
    trackEvent({
      category: ProductAttributesTrackingCategory,
      action: ProductAttributesTrackingAction.ATTRIBUTE_CREATED,
      label: name,
      properties: {
        attributeName: name,
        attributeType: type,
        source
      }
    });
  };

  const handleSubmit = async ({ name, type }: Pick<CoreProductAttribute, 'name' | 'type'>): Promise<IOkOrErrorResult> => {
    trackAttributeCreation({ name, type });

    return performFormMutation({
      mutationFn: () =>
        createAttribute({
          awaitRefetchQueries: true,
          refetchQueries: ['attributes', 'productAttributes'],
          variables: {
            input: {
              accountId,
              name,
              type
            }
          }
        }),
      onError: () => addError()
    });
  };

  const handleSuccess = (attribute: ProductAttribute): void => {
    addSuccess('Product attribute added successfully.');
    onSuccess?.(attribute);
    onClose();
  };

  return (
    <ProductAttributeForm
      buttonText="Create attribute type"
      onCancel={onClose}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
    />
  );
};

export default CreateProductAttribute;
